import { Slide, type SlideProps } from "@mui/material";
import { type FC, type ReactNode, forwardRef, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import type { Experience } from "@/Dashboard/pages/ExperiencesV2/types";
import ExperienceSettingsActions from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsActions";
import ExperienceSettingsContent from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsContent";
import type { ExperienceSettingsFormValues } from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsModal";
import ExperienceSettingsTitle from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsTitle";
import * as S from "@/VisualBuilder/components/ExperienceSettingsDialog/styled";
import { handleTab } from "@/VisualBuilder/components/GlobalTabs/GlobalTabsDataSlice";
import { changeTabStatus } from "@/VisualBuilder/components/MainHeader/MainHeaderDataSlice";
import { EXPERIENCE_STATUS } from "@/constants";
import { ExperienceService } from "@/services";
import { useThunkDispatch } from "@/store/hooks";
import { resetCurrentExperience } from "@/store/v2/currentExperience/actions";
import { currentOrganizationSelector } from "@/store/v2/currentOrganization/currentOrganizationSlice";

const Transition = forwardRef(({ children, ...props }: { children: ReactNode } & SlideProps, ref) => (
  <Slide direction="down" ref={ref} {...props}>
    {children}
  </Slide>
));

interface ExperienceSettingsProps {
  readonly experience: Experience;
  readonly propertyId: string;
  readonly userId: string;
  readonly open: boolean;
  readonly onCloseDialog: () => void;
}

const ExperienceSettings: FC<ExperienceSettingsProps> = ({ experience, propertyId, userId, open, onCloseDialog }) => {
  const dispatch = useThunkDispatch();

  const navigate = useNavigate();

  const { formState, reset, watch } = useFormContext<ExperienceSettingsFormValues>();

  const values = watch();

  const { data: organization } = useSelector(currentOrganizationSelector);
  const organizationId = organization?.id;
  const isOrganizationsSection = location.pathname.startsWith("/organizations");
  const tabPrefix = isOrganizationsSection ? `/organizations/${organizationId}` : "";

  const handleDuplicateExperience = async () => {
    let status = {};

    // When duplicating a template, the new experience should be a draft experience instead of a template
    if (experience.isTemplate) {
      status = { isTemplate: false, status: EXPERIENCE_STATUS.DRAFT };
    }

    const { resp } = await ExperienceService.addDuplicateExperience(Number(propertyId), Number(experience.id), status);

    const id = resp?.id;

    dispatch(resetCurrentExperience());

    const newState = {
      status: "active",
      name: "Loading",
    };

    const tabId = `${tabPrefix}/properties/${propertyId}/experiences/${id}/collect`;

    navigate(tabId);

    dispatch(handleTab({ userId: userId!, tabId, newState }));

    toast.success(`Duplicate created from ${experience.title}`);
  };

  const handleConvertToTemplate = async () => {
    await ExperienceService.updateExperience(Number(propertyId), Number(experience.id), {
      status: EXPERIENCE_STATUS.DRAFT,
      isTemplate: true,
    });

    const newState = {
      status: "active",
      name: "Loading",
    };

    const tabId = `${tabPrefix}/properties/${propertyId}/experiences/${experience.id}/collect`;

    navigate(tabId);

    dispatch(changeTabStatus({ tab: false }));
    dispatch(handleTab({ userId: userId!, tabId, newState }));

    toast.success(`Template created from ${experience.title}`);
  };

  const onSubmit = useCallback(async () => {
    const changedKeys = Object.keys(formState.dirtyFields);

    const changedData = changedKeys.reduce((object: Record<string, unknown>, key) => {
      object[key] = values[key as keyof typeof values];
      return object;
    }, {});

    if (Object.keys(changedData).length) {
      await ExperienceService.updateExperience(Number(propertyId), Number(experience.id), changedData);
    }

    reset({ ...values });
    onCloseDialog();
  }, [propertyId, experience.id, values, formState.dirtyFields, reset]);

  return (
    <S.Dialog
      aria-labelledby={`$experience-settings-dialog-title`}
      aria-describedby={`experience-settings-dialog-description`}
      open={open}
      onClose={onCloseDialog}
      TransitionComponent={Transition}
      maxWidth="lg"
    >
      <ExperienceSettingsTitle onCloseDialog={onCloseDialog} />
      <ExperienceSettingsContent />
      <ExperienceSettingsActions
        onSubmit={onSubmit}
        handleDuplicateExperience={handleDuplicateExperience}
        handleConvertToTemplate={handleConvertToTemplate}
      />
    </S.Dialog>
  );
};

export default ExperienceSettings;
